import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { format, parseISO } from "date-fns";
import type { AggregateBreachCalculation, AggregateBreachClaimantDetail, AccommodationRun } from "@/types";

// ==================== SHARED CONSTANTS ====================

const currencyFormat = '_("$"* #,##0.00_);_("$"* \\(#,##0.00\\);_("$"* "-"??_);_(@_)';

const COLORS = {
  primary: "FF2A7D99",
  primaryDark: "FF1E5C73",
  white: "FFFFFFFF",
  black: "FF1A1A1A",
  textSecondary: "FF6E6E6E",
  rowFillOdd: "FFF5F7FA",
  rowFillEven: "FFFFFFFF",
  subtotalFill: "FFE0EEF3",
  borderLight: "FFD5DAE0",
  borderMedium: "FFB0B8C2",
} as const;

const SHEET_OPTIONS: Partial<ExcelJS.AddWorksheetOptions> = {
  views: [{ showGridLines: false }],
};

const labelFont: Partial<ExcelJS.Font> = { bold: true, size: 10, color: { argb: COLORS.black } };
const valueFont: Partial<ExcelJS.Font> = { size: 10, color: { argb: COLORS.textSecondary } };
const subtotalFont: Partial<ExcelJS.Font> = { bold: true, size: 10, color: { argb: COLORS.primaryDark } };

// ==================== SHARED HELPERS ====================

const thinBorder = (color: string = COLORS.borderLight): Partial<ExcelJS.Borders> => ({
  top: { style: "thin", color: { argb: color } },
  left: { style: "thin", color: { argb: color } },
  bottom: { style: "thin", color: { argb: color } },
  right: { style: "thin", color: { argb: color } },
});

const applyDataCellStyle = (cell: ExcelJS.Cell, fillColor: string, isCurrency = false) => {
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fillColor } };
  cell.border = thinBorder();
  cell.font = { size: 10, color: { argb: COLORS.black } };
  if (isCurrency) cell.numFmt = currencyFormat;
};

const rowFill = (index: number) => (index % 2 === 0 ? COLORS.rowFillOdd : COLORS.rowFillEven);

const applyHeaderRow = (row: ExcelJS.Row, headers: string[]) => {
  row.height = 30;
  headers.forEach((header, idx) => {
    const cell = row.getCell(idx + 1);
    cell.value = header;
    cell.font = { bold: true, size: 10, color: { argb: COLORS.white } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.primary } };
    cell.border = thinBorder(COLORS.primaryDark);
  });
};

const applyTotalsRowStyle = (row: ExcelJS.Row, columnCount: number) => {
  for (let col = 1; col <= columnCount; col++) {
    const cell = row.getCell(col);
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.subtotalFill } };
    cell.border = {
      top: { style: "medium", color: { argb: COLORS.primary } },
      bottom: { style: "thin", color: { argb: COLORS.borderMedium } },
      left: { style: "thin", color: { argb: COLORS.borderLight } },
      right: { style: "thin", color: { argb: COLORS.borderLight } },
    };
  }
};

const buildCsv = (headers: string[], rows: string[][]): string => {
  const escape = (v: string) => (v.includes(",") || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v);
  return [headers, ...rows].map((r) => r.map(escape).join(",")).join("\n");
};

const today = () => format(new Date(), "yyyyMMdd");

// ==================== CSV EXPORTS ====================

// Currency columns are emitted with two decimal places (e.g. "50000.00") to preserve the
// NUMERIC(n,2) shape the API exposes. Excel auto-detects these as numeric so SUM/sort/pivot
// work without manual reformatting. Trade-off: no `$` prefix in plain text viewers —
// acceptable for a data-interchange format.
const money = (v: number) => v.toFixed(2);

export const downloadBreachCalculationsCSV = (calculations: AggregateBreachCalculation[], policyId: string): void => {
  const headers = ["Date", "Breached", "Breach Amount", "Carrier Liability", "Net Carrier Liability", "Superseded"];
  const rows = calculations.map((c) => [
    format(parseISO(c.created_datetime), "M/d/yyyy HH:mm"),
    c.is_breached ? "Yes" : "No",
    money(c.breach_amount),
    money(c.carrier_liability),
    money(c.net_carrier_liability),
    c.is_superseded ? "Yes" : "",
  ]);
  const csv = buildCsv(headers, rows);
  saveAs(
    new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    `Breach_Calculations_${policyId.slice(0, 8)}_${today()}.csv`
  );
};

export const downloadAccommodationRunsCSV = (runs: AccommodationRun[], policyId: string): void => {
  const headers = ["Month", "Eligible Amount", "Submission Threshold"];
  const rows = runs.map((r) => [
    format(parseISO(r.run_date), "MMMM yyyy"),
    money(r.funding_amount),
    money(r.prorated_attachment),
  ]);
  const csv = buildCsv(headers, rows);
  saveAs(
    new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    `Accommodation_Runs_${policyId.slice(0, 8)}_${today()}.csv`
  );
};

// ==================== XLSX REPORT ====================

const buildSummarySheet = (workbook: ExcelJS.Workbook, calculation: AggregateBreachCalculation) => {
  const sheet = workbook.addWorksheet("Summary", SHEET_OPTIONS);
  sheet.columns = [{ width: 28 }, { width: 22 }];

  let rowIdx = 1;

  const titleRow = sheet.getRow(rowIdx++);
  titleRow.height = 36;
  titleRow.getCell(1).value = "Breach Calculation Report";
  titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: COLORS.white } };
  titleRow.getCell(1).alignment = { vertical: "middle" };
  for (let col = 1; col <= 2; col++) {
    titleRow.getCell(col).fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.primary } };
  }

  // Spacer
  sheet.getRow(rowIdx++).height = 8;

  const addMeta = (label: string, value: string) => {
    const row = sheet.getRow(rowIdx++);
    row.height = 26;
    row.getCell(1).value = label;
    row.getCell(1).font = labelFont;
    row.getCell(2).value = value;
    row.getCell(2).font = valueFont;
  };

  const fmt = (v: number) => `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  addMeta("Date:", format(parseISO(calculation.created_datetime), "M/d/yyyy HH:mm"));
  addMeta("Breached:", calculation.is_breached ? "Yes" : "No");
  addMeta("Breach Amount:", fmt(calculation.breach_amount));
  addMeta("Carrier Liability:", fmt(calculation.carrier_liability));
  addMeta("Net Carrier Liability:", fmt(calculation.net_carrier_liability));
  addMeta("Total Claimants:", String(calculation.total_claimant_count));
  addMeta("Total Agg Eligible:", fmt(calculation.total_agg_eligible));
  addMeta("Total Accommodation Funded:", fmt(calculation.total_accommodation_funded));
};

const buildClaimantDetailSheet = (workbook: ExcelJS.Workbook, details: AggregateBreachClaimantDetail[]) => {
  const sheet = workbook.addWorksheet("Claimant Detail", SHEET_OPTIONS);
  const columnWidths = [38, 20, 20, 20, 12, 20, 15];
  sheet.columns = columnWidths.map((width) => ({ width }));

  let rowIdx = 1;

  const titleRow = sheet.getRow(rowIdx++);
  titleRow.height = 32;
  titleRow.getCell(1).value = "Claimant Detail";
  titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: COLORS.white } };
  titleRow.getCell(1).alignment = { vertical: "middle" };
  for (let col = 1; col <= columnWidths.length; col++) {
    titleRow.getCell(col).fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.primary } };
  }

  // Spacer
  sheet.getRow(rowIdx++).height = 8;

  const headers = [
    "Claimant ID",
    "Total Paid",
    "Spec Deductible",
    "Lasered Increment",
    "Lasered",
    "Agg Eligible",
    "Claim Count",
  ];
  applyHeaderRow(sheet.getRow(rowIdx++), headers);

  const dataStartRow = rowIdx;

  details.forEach((d, i) => {
    const row = sheet.getRow(rowIdx++);
    row.height = 24;
    const fill = rowFill(i);

    row.getCell(1).value = d.claimant_id_ref;
    applyDataCellStyle(row.getCell(1), fill);

    row.getCell(2).value = d.claimant_total_paid;
    applyDataCellStyle(row.getCell(2), fill, true);

    row.getCell(3).value = d.effective_spec_deductible;
    applyDataCellStyle(row.getCell(3), fill, true);

    row.getCell(4).value = d.lasered_increment;
    applyDataCellStyle(row.getCell(4), fill, true);

    row.getCell(5).value = d.is_lasered ? "Yes" : "No";
    applyDataCellStyle(row.getCell(5), fill);

    row.getCell(6).value = d.agg_eligible;
    applyDataCellStyle(row.getCell(6), fill, true);

    row.getCell(7).value = d.claim_count;
    applyDataCellStyle(row.getCell(7), fill);
    row.getCell(7).numFmt = "#,##0";
  });

  const dataEndRow = rowIdx - 1;

  if (details.length > 0) {
    sheet.getRow(rowIdx++).height = 6;
    const totalsRow = sheet.getRow(rowIdx++);
    totalsRow.height = 28;
    totalsRow.getCell(1).value = "Totals";
    totalsRow.getCell(1).font = subtotalFont;
    applyTotalsRowStyle(totalsRow, columnWidths.length);

    for (const col of [2, 3, 4, 6]) {
      const cell = totalsRow.getCell(col);
      cell.value = {
        formula: `SUM(${sheet.getCell(dataStartRow, col).address}:${sheet.getCell(dataEndRow, col).address})`,
      };
      cell.numFmt = currencyFormat;
      cell.font = subtotalFont;
    }

    const claimCountCell = totalsRow.getCell(7);
    claimCountCell.value = {
      formula: `SUM(${sheet.getCell(dataStartRow, 7).address}:${sheet.getCell(dataEndRow, 7).address})`,
    };
    claimCountCell.numFmt = "#,##0";
    claimCountCell.font = subtotalFont;
  }
};

export const downloadBreachReport = async (
  calculation: AggregateBreachCalculation,
  details: AggregateBreachClaimantDetail[],
  policyId: string
): Promise<void> => {
  const workbook = new ExcelJS.Workbook();
  buildSummarySheet(workbook, calculation);
  buildClaimantDetailSheet(workbook, details);

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `Breach_Report_${calculation.id.slice(0, 8)}_${policyId.slice(0, 8)}_${today()}.xlsx`);
};
